import React, { memo } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import MoonLoader from "react-spinners/MoonLoader";
import ConversationItem from "./ConversationItem";
import { SERVICE_STATUS, SERVICE_STATUS_CONFIG } from "../../utils/constants";
import { normalizePhone } from "../../utils/formatters";
import "./ConversationList.css";

/**
 * Grupo de conversas por status
 */
const ConversationGroup = memo(({
  title,
  icon,
  count,
  conversations,
  selectedId,
  unreadCounts,
  onSelect,
  color,
  isCollapsible = true,
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  if (conversations.length === 0) return null;

  return (
    <div className="sc-conversation-group">
      <div
        className="sc-conversation-group__header"
        onClick={() => isCollapsible && setIsCollapsed(!isCollapsed)}
      >
        <span
          className="sc-conversation-group__indicator"
          style={{
            backgroundColor: color,
            boxShadow: `0 0 0 3px ${color}22`,
          }}
        />
        <span className="sc-conversation-group__title">{title}</span>
        <span
          className="sc-conversation-group__count"
          style={{
            backgroundColor: `${color}15`,
            color: color,
          }}
        >
          {count}
        </span>
        {isCollapsible && (
          <span className={`sc-conversation-group__arrow ${isCollapsed ? "collapsed" : ""}`}>
            ▾
          </span>
        )}
      </div>

      {!isCollapsed && (
        <div className="sc-conversation-group__items">
          {conversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              isSelected={selectedId === conversation.id}
              unreadCount={unreadCounts[normalizePhone(conversation.phone)] || 0}
              onClick={() => onSelect(conversation)}
            />
          ))}
        </div>
      )}
    </div>
  );
});

/**
 * Lista de conversas agrupada por status
 */
const ConversationList = ({
  groupedConversations,
  selectedConversation,
  unreadCounts,
  onSelectConversation,
  isLoading,
  hasMore,
  onLoadMore,
  isLoadingMore,
  showGrouped = true,
}) => {
  const scrollbarRef = React.useRef(null);

  // Se não for agrupado, mostrar lista plana
  if (!showGrouped) {
    const allConversations = [
      ...(groupedConversations[SERVICE_STATUS.NEW] || []),
      ...(groupedConversations[SERVICE_STATUS.IN_SERVICE] || []),
      ...(groupedConversations[SERVICE_STATUS.WAITING] || []),
      ...(groupedConversations[SERVICE_STATUS.FINISHED] || []),
    ];

    return (
      <div className="sc-conversation-list">
        <PerfectScrollbar ref={scrollbarRef}>
          {isLoading ? (
            <div className="sc-conversation-list__loading">
              <MoonLoader size={30} color="#3b82f6" />
              <span>Carregando conversas...</span>
            </div>
          ) : allConversations.length === 0 ? (
            <div className="sc-conversation-list__empty">
              <span className="sc-conversation-list__empty-icon">💬</span>
              <span>Nenhuma conversa encontrada</span>
            </div>
          ) : (
            <>
              {allConversations.map((conversation) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  isSelected={selectedConversation?.id === conversation.id}
                  unreadCount={unreadCounts[normalizePhone(conversation.phone)] || 0}
                  onClick={() => onSelectConversation(conversation)}
                />
              ))}

              {/* Botão Carregar Mais */}
              {hasMore && (
                <div className="sc-conversation-list__load-more">
                  <button
                    onClick={onLoadMore}
                    disabled={isLoadingMore}
                    className="sc-conversation-list__load-more-btn"
                  >
                    {isLoadingMore ? (
                      <>
                        <MoonLoader size={14} color="#666" />
                        Carregando...
                      </>
                    ) : (
                      "Carregar mais"
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </PerfectScrollbar>
      </div>
    );
  }

  // Lista agrupada por status
  return (
    <div className="sc-conversation-list">
      <PerfectScrollbar ref={scrollbarRef}>
        {isLoading ? (
          <div className="sc-conversation-list__loading">
            <MoonLoader size={30} color="#3b82f6" />
            <span>Carregando conversas...</span>
          </div>
        ) : (
          <>
            {/* Novos - Com destaque */}
            <ConversationGroup
              title="Novos"
              icon={SERVICE_STATUS_CONFIG[SERVICE_STATUS.NEW].icon}
              count={groupedConversations[SERVICE_STATUS.NEW]?.length || 0}
              conversations={groupedConversations[SERVICE_STATUS.NEW] || []}
              selectedId={selectedConversation?.id}
              unreadCounts={unreadCounts}
              onSelect={onSelectConversation}
              color={SERVICE_STATUS_CONFIG[SERVICE_STATUS.NEW].color}
            />

            {/* Em Atendimento */}
            <ConversationGroup
              title="Em Atendimento"
              icon={SERVICE_STATUS_CONFIG[SERVICE_STATUS.IN_SERVICE].icon}
              count={groupedConversations[SERVICE_STATUS.IN_SERVICE]?.length || 0}
              conversations={groupedConversations[SERVICE_STATUS.IN_SERVICE] || []}
              selectedId={selectedConversation?.id}
              unreadCounts={unreadCounts}
              onSelect={onSelectConversation}
              color={SERVICE_STATUS_CONFIG[SERVICE_STATUS.IN_SERVICE].color}
            />

            {/* Aguardando */}
            <ConversationGroup
              title="Aguardando"
              icon={SERVICE_STATUS_CONFIG[SERVICE_STATUS.WAITING].icon}
              count={groupedConversations[SERVICE_STATUS.WAITING]?.length || 0}
              conversations={groupedConversations[SERVICE_STATUS.WAITING] || []}
              selectedId={selectedConversation?.id}
              unreadCounts={unreadCounts}
              onSelect={onSelectConversation}
              color={SERVICE_STATUS_CONFIG[SERVICE_STATUS.WAITING].color}
            />

            {/* Finalizados - Colapsado por padrão */}
            {groupedConversations[SERVICE_STATUS.FINISHED]?.length > 0 && (
              <ConversationGroup
                title="Finalizados"
                icon={SERVICE_STATUS_CONFIG[SERVICE_STATUS.FINISHED].icon}
                count={groupedConversations[SERVICE_STATUS.FINISHED]?.length || 0}
                conversations={groupedConversations[SERVICE_STATUS.FINISHED] || []}
                selectedId={selectedConversation?.id}
                unreadCounts={unreadCounts}
                onSelect={onSelectConversation}
                color={SERVICE_STATUS_CONFIG[SERVICE_STATUS.FINISHED].color}
              />
            )}

            {/* Botão Carregar Mais */}
            {hasMore && (
              <div className="sc-conversation-list__load-more">
                <button
                  onClick={onLoadMore}
                  disabled={isLoadingMore}
                  className="sc-conversation-list__load-more-btn"
                >
                  {isLoadingMore ? (
                    <>
                      <MoonLoader size={14} color="#666" />
                      Carregando...
                    </>
                  ) : (
                    "Carregar mais"
                  )}
                </button>
              </div>
            )}

            {/* Mensagem se não houver conversas */}
            {Object.values(groupedConversations).every((g) => g?.length === 0) && (
              <div className="sc-conversation-list__empty">
                <span className="sc-conversation-list__empty-icon">💬</span>
                <span>Nenhuma conversa encontrada</span>
              </div>
            )}
          </>
        )}
      </PerfectScrollbar>
    </div>
  );
};

ConversationGroup.displayName = "ConversationGroup";

export default ConversationList;
